import React from "react";
import { Avatar, Box, Chip, Stack, Tooltip, Typography } from "@mui/joy";
import {
  CalendarMonthIcon,
  EventIcon,
  LinkIcon,
  OpenInNewIcon,
  PeopleIcon,
  SDelete,
  SEdit,
  VisibilityIcon,
  WifiIcon,
  WifiOffIcon,
} from "../../../styles/StyledMUI";
import {
  colorTokens,
  gradientListRowCardShellSx,
  listRowCompactIconButtonSx,
  listRowDetailItemStackedSx,
  listRowDetailsGridSx,
  listRowPublicLinkActionsSx,
  listRowPublicLinkTextSx,
  listRowPublicLinkValueRowSx,
  listRowFieldLabelEmphasisWithIconSx,
  listRowFieldLabelWithIconSx,
  listRowIconButtonPlainSx,
} from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import type { Event } from "../../../api/events/Event.typed";
import { formatEventCalendarDate } from "../eventDateHelpers";
import { eventLifeStatus, eventStatusColor, eventStatusLabel } from "../eventStatusHelpers";

type Props = {
  event: Event;
  canEdit: boolean;
  onEdit: () => void;
  onParticipants: () => void;
  onDelete: () => void;
  onToggleOnline?: (online: boolean) => void;
};

type DetailStackedProps = {
  label: string;
  icon: React.ReactNode;
  value: string;
  emphasis?: boolean;
};

function ListRowDetailStacked({ label, icon, value, emphasis }: DetailStackedProps) {
  return (
    <Box sx={listRowDetailItemStackedSx}>
      <Box sx={emphasis ? listRowFieldLabelEmphasisWithIconSx : listRowFieldLabelWithIconSx}>
        {icon}
        {label}
      </Box>
      <DarkTypography level="body-xs" colorVariant={emphasis ? "title" : "muted"}>
        {value}
      </DarkTypography>
    </Box>
  );
}

export default function EventCard({
  event,
  canEdit,
  onEdit,
  onParticipants,
  onDelete,
  onToggleOnline,
}: Props) {
  const status = eventLifeStatus(event);

  const handleCopyLink = () => {
    if (!event.public_url) return;
    navigator.clipboard.writeText(event.public_url).catch(() => undefined);
  };

  return (
    <Box sx={gradientListRowCardShellSx}>
      <Box sx={{ p: 1 }}>
        {/* Top row: avatar + title/chip + CTAs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{
                display: { xs: "none", sm: "flex" },
                width: 40,
                height: 40,
                flexShrink: 0,
                bgcolor: colorTokens.bg.surfaceMuted,
                color: colorTokens.accent.primary,
                border: `1px solid ${colorTokens.border.default}`,
              }}
              aria-hidden
            >
              <EventIcon sx={{ fontSize: "1.25rem" }} />
            </Avatar>

            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <DarkTypography
                  level="title-md"
                  colorVariant="primary"
                  sx={{
                    fontWeight: 700,
                    minWidth: 0,
                    overflow: "hidden",
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    textOverflow: { sm: "ellipsis" },
                  }}
                >
                  {event.name || "Événement sans nom"}
                </DarkTypography>
                <Chip
                  size="sm"
                  variant="soft"
                  color={eventStatusColor(status)}
                  sx={{ flexShrink: 0 }}
                >
                  {eventStatusLabel(status)}
                </Chip>
                <Chip
                  size="sm"
                  variant="soft"
                  color={event.is_online ? "success" : "neutral"}
                  sx={{ flexShrink: 0 }}
                  startDecorator={
                    event.is_online ? (
                      <WifiIcon sx={{ fontSize: "0.8rem" }} />
                    ) : (
                      <WifiOffIcon sx={{ fontSize: "0.8rem" }} />
                    )
                  }
                >
                  {event.is_online ? "En ligne" : "Hors-ligne"}
                </Chip>
              </Box>
              <DarkTypography level="body-xs" colorVariant="muted">
                Événement #{event.id} ·{" "}
                {event.participants_count ?? event.participants?.length ?? 0} participant(s)
                {event.winners?.length ? ` · ${event.winners.length} gagnant(s)` : ""}
              </DarkTypography>
            </Stack>
          </Box>

          {/* CTAs — right side, aligned with title row */}
          <Box
            sx={{
              display: "flex",
              gap: 0.3,
              flexShrink: 0,
              alignItems: "center",
              ml: "auto",
            }}
          >
            {canEdit && onToggleOnline && (
              <Tooltip title={event.is_online ? "Mettre hors-ligne" : "Mettre en ligne"}>
                <IconButtonCTA
                  ctaVariant={event.is_online ? "danger" : "success"}
                  size="sm"
                  variant="plain"
                  aria-label={event.is_online ? "Mettre hors-ligne" : "Mettre en ligne"}
                  sx={listRowIconButtonPlainSx}
                  onClick={() => onToggleOnline(!event.is_online)}
                >
                  {event.is_online ? (
                    <WifiIcon sx={{ fontSize: "1.1rem" }} />
                  ) : (
                    <WifiOffIcon sx={{ fontSize: "1.1rem" }} />
                  )}
                </IconButtonCTA>
              </Tooltip>
            )}

            <Tooltip title="Participants">
              <IconButtonCTA
                ctaVariant="accent"
                size="sm"
                variant="plain"
                aria-label="Participants"
                sx={listRowIconButtonPlainSx}
                onClick={onParticipants}
              >
                <PeopleIcon sx={{ fontSize: "1.1rem" }} />
              </IconButtonCTA>
            </Tooltip>

            {canEdit && (
              <>
                <Tooltip title="Modifier">
                  <IconButtonCTA
                    ctaVariant="accent"
                    size="sm"
                    variant="plain"
                    aria-label="Modifier"
                    sx={listRowIconButtonPlainSx}
                    onClick={onEdit}
                  >
                    <SEdit />
                  </IconButtonCTA>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButtonCTA
                    ctaVariant="danger"
                    size="sm"
                    variant="plain"
                    aria-label="Supprimer"
                    sx={listRowIconButtonPlainSx}
                    onClick={onDelete}
                  >
                    <SDelete />
                  </IconButtonCTA>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box sx={listRowDetailsGridSx}>
          <ListRowDetailStacked
            label="Début"
            icon={<CalendarMonthIcon sx={{ fontSize: "0.7rem" }} />}
            value={formatEventCalendarDate(event.start_date)}
          />
          <ListRowDetailStacked
            label="Fin"
            icon={<CalendarMonthIcon sx={{ fontSize: "0.7rem" }} />}
            value={formatEventCalendarDate(event.end_date)}
          />
          {event.draw_date && (
            <ListRowDetailStacked
              label="Tirage"
              icon={<CalendarMonthIcon sx={{ fontSize: "0.7rem" }} />}
              value={formatEventCalendarDate(event.draw_date)}
              emphasis
            />
          )}
          {event.public_url && (
            <Box sx={listRowDetailItemStackedSx}>
              <Box sx={listRowFieldLabelWithIconSx}>
                <LinkIcon sx={{ fontSize: "0.7rem" }} />
                Lien public
              </Box>
              <Box sx={listRowPublicLinkValueRowSx}>
                <Typography
                  component="a"
                  href={event.public_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  level="body-xs"
                  sx={listRowPublicLinkTextSx}
                >
                  {event.public_url}
                </Typography>
                <Box sx={listRowPublicLinkActionsSx}>
                  <Tooltip title="Copier le lien">
                    <IconButtonCTA
                      ctaVariant="accent"
                      size="sm"
                      variant="plain"
                      aria-label="Copier le lien public"
                      sx={listRowCompactIconButtonSx}
                      onClick={handleCopyLink}
                    >
                      <LinkIcon sx={{ fontSize: "0.9rem" }} />
                    </IconButtonCTA>
                  </Tooltip>
                  <Tooltip title="Ouvrir dans un nouvel onglet">
                    <Box
                      component="a"
                      href={event.public_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "inline-flex", flexShrink: 0 }}
                    >
                      <IconButtonCTA
                        ctaVariant="accent"
                        size="sm"
                        variant="plain"
                        aria-label="Ouvrir la page publique"
                        sx={listRowCompactIconButtonSx}
                      >
                        <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
                      </IconButtonCTA>
                    </Box>
                  </Tooltip>
                  {canEdit && event.preview_url && (
                    <Tooltip title="Prévisualiser la page publique">
                      <Box
                        component="a"
                        href={event.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: "inline-flex", flexShrink: 0 }}
                      >
                        <IconButtonCTA
                          ctaVariant="accent"
                          size="sm"
                          variant="plain"
                          aria-label="Prévisualiser la page publique"
                          sx={listRowCompactIconButtonSx}
                        >
                          <VisibilityIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButtonCTA>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
